'use client'

import { isNil, isUndefined } from 'lodash-es'
import { type RefObject, useEffect } from 'react'

const MIN_COLLAPSED_HEIGHT = 0

/** translate3d + 정수 px로 컴포지터 활용·서브픽셀 비용 감소 */
const setSheetTransform = (sheet: HTMLDivElement, translateY: number) => {
  const px = Math.round(translateY)
  sheet.style.transform = `translate3d(0,${px}px,0)`
}

type Params = {
  sheetRef: RefObject<HTMLDivElement | null>
  collapsedHeightProp: number | undefined
  defaultCollapsed: boolean | undefined
  defaultCollapsedHeight: number | undefined
  maxSheetYRef: RefObject<number>
  currentSheetYRef: RefObject<number>
  /** 시트가 초기 기본 위치 상태인지 설정 */
  setAtDefaultPosition: (value: boolean) => void
  setCollapsed: (value: boolean) => void
}

export const useSheetResize = ({
  sheetRef,
  collapsedHeightProp,
  defaultCollapsed,
  defaultCollapsedHeight,
  maxSheetYRef,
  currentSheetYRef,
  setAtDefaultPosition,
  setCollapsed,
}: Params) => {
  useEffect(() => {
    const sheet = sheetRef.current
    if (!sheet) return

    const updateMaxSheetY = () => {
      const sheetEl = sheetRef.current
      if (!sheetEl) return

      const { height } = sheetEl.getBoundingClientRect()
      const collapsedHeight = collapsedHeightProp ?? MIN_COLLAPSED_HEIGHT

      maxSheetYRef.current = Math.max(0, height - collapsedHeight)
    }

    const observer = new ResizeObserver(() => {
      const sheet = sheetRef.current
      const dataCollapsed = sheet?.dataset.collapsed

      // 1) 최초 렌더: data-collapsed 미설정 → setCollapsed(true)로 속성 주입 후 종료
      if (!sheet || isNil(dataCollapsed)) {
        setCollapsed(true)

        return
      }

      // 2) maxY 재계산 — 시트 실제 높이가 변했을 때만 초기 위치 상태 해제
      // 필터 스크롤로 인한 ResizeObserver 트리거 시에는 maxY가 변하지 않으므로 리셋하지 않음
      const prevMaxY = maxSheetYRef.current
      updateMaxSheetY()

      if (maxSheetYRef.current !== prevMaxY) {
        setAtDefaultPosition(false)
      }

      // 3) 현재 접힘 상태에 맞게 transform 재적용 (orientation 전환 등)
      const isCollapsed = sheet.dataset.collapsed === 'true'

      setSheetTransform(sheet, isCollapsed ? maxSheetYRef.current : 0)
    })

    observer.observe(sheet)

    updateMaxSheetY()

    // defaultCollapsed=true일 때 초기 1회만 시트를 targetY로 내림
    if (defaultCollapsed && currentSheetYRef.current === 0) {
      const { height } = sheet.getBoundingClientRect()
      const targetY = isUndefined(defaultCollapsedHeight)
        ? maxSheetYRef.current
        : Math.max(height - defaultCollapsedHeight, 0)

      // targetY가 완전히 접히지도 펼쳐지지도 않은 값이면 → 반만 열린 초기 위치
      if (targetY !== 0 && targetY !== maxSheetYRef.current) {
        setAtDefaultPosition(true)
      }

      currentSheetYRef.current = targetY
      setSheetTransform(sheet, targetY)
    }

    sheet.style.visibility = 'visible'

    return () => {
      observer.disconnect()
    }
  }, [collapsedHeightProp])
}
